import {useEffect, useRef, useState} from 'react';
import {PERSONAS, usePersonas} from '../../config/personas';
import {RU_TIMEZONES, detectRuTimezone, findRuTimezone} from '../../config/timezones';
import {generateZvonokAudioPreview, generateZvonokText, scheduleZvonok} from '../../api/zvonki';
import {trackEvent} from '../../utils/analytics';
import {showError} from '../../utils/notify';

// Карточка звезды — как в видео-разделе, но без видео-превью.
function PersonaCard({persona, isSelected, onSelect}) {
    return (
        <div
            onClick={() => onSelect(persona)}
            className="flex-shrink-0 cursor-pointer w-[140px]"
        >
            <div
                className={`rounded-[20px] bg-white w-full relative transition-all p-[10px] ${isSelected ? 'ring-2 ring-brand-red ring-offset-0' : ''}`}>
                <div className="h-[180px] rounded-[10px] overflow-hidden relative">
                    <img
                        src={persona.image}
                        alt={persona.name}
                        className="w-full h-full object-cover absolute inset-0"
                    />
                </div>
                <div className="pt-[10px] flex items-end justify-between">
                    <p className="text-sm font-medium text-brand-dark leading-[1.3] tracking-[-0.14px] w-[85px]">
                        {persona.name}
                    </p>
                    {isSelected && (
                        <div
                            className="w-6 h-6 rounded-full bg-gradient-to-r from-brand-red via-brand-pink to-brand-orange flex items-center justify-center">
                            <svg width="12" height="10" viewBox="0 0 12 10" fill="none">
                                <path d="M1 5L4.5 8.5L11 1" stroke="white" strokeWidth="2" strokeLinecap="round"
                                      strokeLinejoin="round"/>
                            </svg>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// Кружок плеера в плашке превью: серый — озвучки для текущего текста нет,
// градиентный — превью готово; иконка меняется на паузу во время игры.
function PreviewPlayCircle({active = false, playing = false, loading = false}) {
    return (
        <span
            className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                active
                    ? 'bg-gradient-to-r from-brand-red via-brand-pink to-brand-orange text-white'
                    : 'bg-brand-dark/10 text-brand-dark/40'
            }`}
        >
            {loading ? (
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10"
                            stroke="currentColor" strokeWidth="4" fill="none"/>
                    <path className="opacity-75" fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                </svg>
            ) : playing ? (
                <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
                    <rect x="2" y="2" width="3.5" height="10" rx="1"/>
                    <rect x="8.5" y="2" width="3.5" height="10" rx="1"/>
                </svg>
            ) : (
                <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
                    <path d="M4 2.5l8 4.5-8 4.5v-9z"/>
                </svg>
            )}
        </span>
    );
}

// Scroll Arrows
function ScrollArrows({onPrev, onNext}) {
    return (
        <div className="flex gap-[5px]">
            <button
                onClick={onPrev}
                className="w-[40px] h-[28px] rounded-lg bg-white flex items-center justify-center hover:bg-gray-50 transition"
            >
                <svg width="8" height="14" viewBox="0 0 8 14" fill="none">
                    <path d="M7 1L1 7L7 13" stroke="#221d2d" strokeWidth="2" strokeLinecap="round"
                          strokeLinejoin="round"/>
                </svg>
            </button>
            <button
                onClick={onNext}
                className="w-[40px] h-[28px] rounded-lg bg-white flex items-center justify-center hover:bg-gray-50 transition"
            >
                <svg width="8" height="14" viewBox="0 0 8 14" fill="none">
                    <path d="M1 1L7 7L1 13" stroke="#221d2d" strokeWidth="2" strokeLinecap="round"
                          strokeLinejoin="round"/>
                </svg>
            </button>
        </div>
    );
}

// Свои ключи, чтобы данные звонка не смешивались с другими разделами.
// Номер, дата и пояс переживают перезагрузку страницы (например, возврат с оплаты).
const GENERATED_TEXTS_KEY = 'zvonki_generated_texts';
const PHONE_KEY = 'zvonki_phone_digits';
const SCHEDULED_AT_KEY = 'zvonki_scheduled_at_local';
const TIMEZONE_KEY = 'zvonki_timezone';
const PERSONA_KEY = 'zvonki_celeb_id';
// Готовое превью тоже переживает перезагрузку (файл уже на S3) — не гоняем
// TTS заново и не заставляем человека повторно жать «Сгенерировать».
const PREVIEW_KEY = 'zvonki_preview';

// Максимум совпадает с бэкендом: ~500 символов ≈ 40 секунд речи.
const MAX_TEXT_LENGTH = 500;

// Ограничения на время звонка (совпадают с бэкендом: ≥10 мин, ≤30 дней;
// на фронте даём запас — минимум через 15 минут).
const MIN_LEAD_MINUTES = 15;
const MAX_LEAD_DAYS = 30;

// Введённое в <input type="datetime-local"> время трактуем как СТЕННОЕ время
// выбранного российского пояса, а не пояса браузера: москвич планирует звонок
// маме во Владивосток на «09:00» по владивостокскому времени. Смещения поясов
// фиксированные (DST в РФ нет), поэтому достаточно простой арифметики.

// Стенное время 'YYYY-MM-DDTHH:mm' момента ms в поясе со смещением offsetHours.
function toZoneInputValue(ms, offsetHours) {
    const d = new Date(ms + offsetHours * 3600 * 1000);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}T${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
}

// Момент времени (ms) для стенного 'YYYY-MM-DDTHH:mm' в поясе offsetHours.
// На мусорном входе возвращает NaN.
function zoneInputToMs(value, offsetHours) {
    const [datePart, timePart] = String(value).split('T');
    const [y, m, d] = (datePart || '').split('-').map(Number);
    const [hh, mm] = (timePart || '').split(':').map(Number);
    return Date.UTC(y, m - 1, d, hh, mm) - offsetHours * 3600 * 1000;
}

// Десять цифр местного номера → «+7 (999) 123-45-67» по мере ввода.
function formatPhoneDisplay(digits) {
    if (!digits) return '';
    let out = '+7';
    if (digits.length > 0) out += ` (${digits.slice(0, 3)}`;
    if (digits.length >= 3) out += ')';
    if (digits.length > 3) out += ` ${digits.slice(3, 6)}`;
    if (digits.length > 6) out += `-${digits.slice(6, 8)}`;
    if (digits.length > 8) out += `-${digits.slice(8, 10)}`;
    return out;
}

// Любой ввод → десять цифр местного номера. Сначала срезаем префикс «+7»
// (он есть в уже отформатированном значении поля), потом ведущие 7/8 у
// вставленных 11-значных номеров.
function extractPhoneDigits(raw) {
    let s = raw.trim();
    if (s.startsWith('+7')) s = s.slice(2);
    let digits = s.replace(/\D/g, '');
    if (digits.length === 11 && (digits.startsWith('7') || digits.startsWith('8'))) {
        digits = digits.slice(1);
    }
    return digits.slice(0, 10);
}

// Main Interface Card for celebrity calls
export default function CallMainCard({
                                         credits,
                                         onScheduled,
                                         onAccountStatusUpdate,
                                         onNoCredits,
                                     }) {
    // Live catalog from the admin (static fallback until it loads)
    const personas = usePersonas();

    const [selectedPersona, setSelectedPersona] = useState(() => {
        return PERSONAS.find(p => p.id === localStorage.getItem(PERSONA_KEY)) || PERSONAS[0];
    });

    // Когда приезжает живой каталог, пересаживаем выбор на его объект (или на
    // первую звезду, если выбранной больше нет на сайте).
    useEffect(() => {
        setSelectedPersona(prev => personas.find(p => p.id === prev?.id) || personas[0]);
    }, [personas]);

    // Тексты храним по звёздам ({celebId: text}): смена звезды очищает поле,
    // возврат к звезде с уже сгенерированным текстом восстанавливает его.
    const [generatedTexts, setGeneratedTexts] = useState(() => {
        try {
            const saved = JSON.parse(localStorage.getItem(GENERATED_TEXTS_KEY) || '{}');
            if (saved && typeof saved === 'object' && !Array.isArray(saved)) {
                return Object.fromEntries(
                    Object.entries(saved).filter(([, text]) => typeof text === 'string')
                );
            }
        } catch {
            // повреждённое значение — просто начинаем без текстов
        }
        return {};
    });
    const generatedText = generatedTexts[selectedPersona.id] || '';
    // Пишет текст под звезду, выбранную на момент вызова: если человек
    // переключил звезду, пока шла генерация, результат не попадёт к новой.
    const setGeneratedText = (text) => {
        setGeneratedTexts(prev => ({...prev, [selectedPersona.id]: text}));
    };

    // Текст правится только в явном режиме (Править → Сохранить/Отмена):
    // до сохранения показанный текст не меняется и превью остаётся
    // актуальным, черновик живёт отдельно.
    const [isEditingText, setIsEditingText] = useState(false);
    const [draftText, setDraftText] = useState('');

    // Одно свободное поле: кому звоним, повод, детали — всё сразу. Из него
    // одной кнопкой генерим и текст, и аудиопревью.
    const [aiDetails, setAiDetails] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);
    // Два шага: 1 — звезда + текст + превью («Мне нравится — продолжить»),
    // 2 — номер, время и планирование. Так карточка не пустует слева.
    const [step, setStep] = useState(1);

    const [phoneDigits, setPhoneDigits] = useState(() => {
        return extractPhoneDigits(localStorage.getItem(PHONE_KEY) || '');
    });
    // Ошибки телефона и даты показываются у самих полей (не alert'ом): при
    // сабмите с пустым/невалидным значением фокусируем инпут и подсвечиваем.
    const [phoneError, setPhoneError] = useState(null);
    const phoneInputRef = useRef(null);
    const [dateError, setDateError] = useState(null);
    const dateInputRef = useRef(null);

    const focusField = (ref) => {
        ref.current?.scrollIntoView?.({block: 'center', behavior: 'smooth'});
        ref.current?.focus({preventScroll: true});
    };
    // Пояс восстанавливаем раньше даты: сохранённое стенное время читается
    // именно в нём.
    const [timeZone, setTimeZone] = useState(() => {
        return findRuTimezone(localStorage.getItem(TIMEZONE_KEY) || '') || detectRuTimezone();
    });
    const [scheduledAtLocal, setScheduledAtLocal] = useState(() => {
        // Восстанавливаем только ещё актуальную дату — прошедшую отбрасываем.
        const saved = localStorage.getItem(SCHEDULED_AT_KEY) || '';
        if (!saved) return '';
        const savedZone = findRuTimezone(localStorage.getItem(TIMEZONE_KEY) || '') || detectRuTimezone();
        const ms = zoneInputToMs(saved, savedZone.offset);
        return !Number.isNaN(ms) && ms >= Date.now() + MIN_LEAD_MINUTES * 60 * 1000 ? saved : '';
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Аудиопревью обязательно перед планированием: это же аудио прозвучит в
    // звонке (audio_url с S3 уходит в /schedule или на покупку). preview
    // в состоянии, а не в ref — от его актуальности зависит кнопка сабмита.
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewPlaying, setPreviewPlaying] = useState(false);
    const [previewError, setPreviewError] = useState(null);
    // {key, audioUrl, durationSec, blobUrl}; после перезагрузки blobUrl нет —
    // играем прямо с S3 (audioUrl публичный).
    const [preview, setPreview] = useState(() => {
        try {
            const saved = JSON.parse(localStorage.getItem(PREVIEW_KEY) || 'null');
            if (saved && typeof saved.key === 'string' && typeof saved.audioUrl === 'string') {
                return {
                    key: saved.key,
                    audioUrl: saved.audioUrl,
                    durationSec: saved.durationSec ?? null,
                    blobUrl: null,
                };
            }
        } catch {
            // повреждённое значение — просто начинаем без превью
        }
        return null;
    });
    const previewAudioRef = useRef(null);
    const previewBlobUrlRef = useRef(null);

    const personasRef = useRef(null);

    // Превью привязано к паре «звезда + текст»: смена любого из них требует
    // послушать заново, и только тогда открывается планирование.
    const currentPreviewKey = `${selectedPersona.id}::${generatedText.trim()}`;
    const previewReady = preview != null && preview.key === currentPreviewKey;

    // Только min: атрибут max серит даты в нативном календаре, и люди думают,
    // что следующий месяц выбрать нельзя. Верхнюю границу проверяем на сабмите.
    const minDateTime = toZoneInputValue(Date.now() + MIN_LEAD_MINUTES * 60 * 1000, timeZone.offset);

    // Дата дня «сегодня + dayOffset» в стенном времени выбранного пояса.
    const zoneDayDate = (dayOffset) => {
        return toZoneInputValue(Date.now() + dayOffset * 24 * 3600 * 1000, timeZone.offset).slice(0, 10);
    };

    // «Сегодня»/«Завтра»: подставляем день (в выбранном поясе!), сохраняя уже
    // выбранное время; если время не выбрано или уже прошло — ближайший слот.
    const setQuickDate = (dayOffset) => {
        const prevTime = scheduledAtLocal.split('T')[1];
        const targetWall = `${zoneDayDate(dayOffset)}T${prevTime || '12:00'}`;
        let targetMs = zoneInputToMs(targetWall, timeZone.offset);
        const minAllowedMs = Date.now() + MIN_LEAD_MINUTES * 60 * 1000;
        if (Number.isNaN(targetMs) || targetMs < minAllowedMs) {
            targetMs = Math.ceil(minAllowedMs / (5 * 60 * 1000)) * 5 * 60 * 1000;
        }
        setScheduledAtLocal(toZoneInputValue(targetMs, timeZone.offset));
        setDateError(null);
    };

    const isQuickDateActive = (dayOffset) => {
        return !!scheduledAtLocal && scheduledAtLocal.slice(0, 10) === zoneDayDate(dayOffset);
    };

    // Save generated texts to localStorage when they change
    useEffect(() => {
        localStorage.setItem(GENERATED_TEXTS_KEY, JSON.stringify(generatedTexts));
    }, [generatedTexts]);

    // Номер и дата тоже переживают перезагрузку страницы.
    useEffect(() => {
        localStorage.setItem(PHONE_KEY, phoneDigits);
    }, [phoneDigits]);

    useEffect(() => {
        localStorage.setItem(SCHEDULED_AT_KEY, scheduledAtLocal);
    }, [scheduledAtLocal]);

    useEffect(() => {
        localStorage.setItem(TIMEZONE_KEY, timeZone.id);
    }, [timeZone]);

    useEffect(() => {
        localStorage.setItem(PERSONA_KEY, selectedPersona.id);
    }, [selectedPersona]);

    useEffect(() => {
        // blobUrl не сериализуем — после перезагрузки хватит audioUrl с S3.
        if (preview) {
            localStorage.setItem(PREVIEW_KEY, JSON.stringify({
                key: preview.key,
                audioUrl: preview.audioUrl,
                durationSec: preview.durationSec,
            }));
        }
    }, [preview]);

    const stopPreview = () => {
        previewAudioRef.current?.pause();
        previewAudioRef.current = null;
        setPreviewPlaying(false);
    };

    // Drop the preview audio on unmount
    useEffect(() => () => {
        previewAudioRef.current?.pause();
        if (previewBlobUrlRef.current) URL.revokeObjectURL(previewBlobUrlRef.current);
    }, []);

    const playUrl = (url) => {
        stopPreview();
        const audio = new Audio(url);
        previewAudioRef.current = audio;
        setPreviewPlaying(true);
        audio.onended = () => setPreviewPlaying(false);
        audio.play().catch(() => setPreviewPlaying(false));
    };

    // TTS-превью для готового текста + автовоспроизведение. Общий шаг для
    // основного пути («Сгенерировать» из деталей) и переозвучки из редактора.
    const generatePreviewForText = async (text) => {
        stopPreview();
        trackEvent('generate_zvonok_preview');
        setPreviewLoading(true);
        setPreviewError(null);
        try {
            const data = await generateZvonokAudioPreview({celebId: selectedPersona.id, text});
            const audioBlob = new Blob(
                [Uint8Array.from(atob(data.audio_base64), c => c.charCodeAt(0))],
                {type: 'audio/mpeg'}
            );
            if (previewBlobUrlRef.current) URL.revokeObjectURL(previewBlobUrlRef.current);
            previewBlobUrlRef.current = URL.createObjectURL(audioBlob);
            setPreview({
                key: `${selectedPersona.id}::${text}`,
                audioUrl: data.audio_url,
                durationSec: data.duration_sec ?? null,
                blobUrl: previewBlobUrlRef.current,
            });
            playUrl(previewBlobUrlRef.current);
        } catch (error) {
            console.error('Preview error:', error);
            if (error.status === 429) {
                setPreviewError('Слишком много превью подряд. Подождите минуту.');
            } else {
                setPreviewError('Не получилось сделать превью. Попробуйте ещё раз.');
            }
        } finally {
            setPreviewLoading(false);
        }
    };

    // Основной путь: детали → ИИ-текст → аудиопревью, всё одной кнопкой.
    const handleGenerate = async () => {
        const details = aiDetails.trim();
        if (!details) {
            showError('Расскажите, кому звоним и по какому поводу');
            return;
        }

        trackEvent('generate_zvonok_text');
        setIsGenerating(true);
        try {
            const data = await generateZvonokText({
                celebId: selectedPersona.id,
                details,
            });
            const text = (data.text || '').slice(0, MAX_TEXT_LENGTH).trim();
            setGeneratedText(text);
            setIsEditingText(false);
            await generatePreviewForText(text);
        } catch (error) {
            console.error('AI generation error:', error);
            showError('Ошибка генерации текста. Попробуйте ещё раз.');
        } finally {
            setIsGenerating(false);
        }
    };

    // Переозвучка сохранённого текста: без ИИ, читаем то, что показано.
    const handleRegenerateFromText = async () => {
        const text = generatedText.trim();
        if (!text) {
            showError('Напишите текст звонка');
            return;
        }
        await generatePreviewForText(text);
    };

    // Смена звезды: текст очищается/восстанавливается сам (generatedTexts по
    // celebId), а звук и незаконченную правку останавливаем — черновик
    // принадлежит прошлой звезде.
    const handleSelectPersona = (persona) => {
        if (persona.id === selectedPersona.id) return;
        stopPreview();
        setIsEditingText(false);
        setPreviewError(null);
        setSelectedPersona(persona);
    };

    const startEditing = () => {
        stopPreview();
        setDraftText(generatedText);
        setIsEditingText(true);
    };

    const cancelEditing = () => setIsEditingText(false);

    // Сохранение правки сразу озвучивает новый текст — плашка это и обещает
    // («Сохраните — и озвучим заново»). Неизменённый текст не переозвучиваем.
    const saveEditedText = async () => {
        const text = draftText.trim();
        if (!text) {
            showError('Напишите текст звонка');
            return;
        }
        setGeneratedText(text);
        setIsEditingText(false);
        if (preview?.key !== `${selectedPersona.id}::${text}`) {
            await generatePreviewForText(text);
        }
    };

    const handleTogglePlay = () => {
        if (previewPlaying) {
            stopPreview();
            return;
        }
        // Восстановленное из localStorage превью играет прямо с S3.
        if (previewReady) playUrl(preview.blobUrl || preview.audioUrl);
    };

    const handleSubmit = async () => {
        const text = generatedText.trim();
        if (!text || !previewReady) {
            showError('Сначала сгенерируйте превью — именно это аудио прозвучит в звонке');
            return;
        }
        if (phoneDigits.length !== 10) {
            setPhoneError(phoneDigits.length === 0
                ? 'Укажите номер телефона'
                : 'Введите номер полностью: +7 (999) 123-45-67');
            focusField(phoneInputRef);
            return;
        }
        if (!scheduledAtLocal) {
            setDateError('Выберите дату и время звонка');
            focusField(dateInputRef);
            return;
        }
        // Стенное время выбранного пояса → абсолютный момент звонка.
        const scheduledMs = zoneInputToMs(scheduledAtLocal, timeZone.offset);
        if (Number.isNaN(scheduledMs)
            || scheduledMs < Date.now() + (MIN_LEAD_MINUTES - 5) * 60 * 1000) {
            setDateError(`Выберите время хотя бы через ${MIN_LEAD_MINUTES} минут`);
            focusField(dateInputRef);
            return;
        }
        if (scheduledMs > Date.now() + MAX_LEAD_DAYS * 24 * 60 * 60 * 1000) {
            setDateError(`Выберите дату в ближайшие ${MAX_LEAD_DAYS} дней`);
            focusField(dateInputRef);
            return;
        }

        // Полностью валидная заявка: используется и для планирования, и для
        // пейвола (там она уедет вместе с покупкой — вебхук запланирует сам).
        // audioUrl — S3-файл прослушанного превью, он и прозвучит в звонке.
        const callPayload = {
            celebId: selectedPersona.id,
            phone: `+7${phoneDigits}`,
            callText: text,
            audioUrl: preview.audioUrl,
            audioDurationSec: preview.durationSec,
            scheduledAt: new Date(scheduledMs).toISOString(),
            timezone: timeZone.id,
        };

        // Check if user has call credits
        if (credits <= 0) {
            onNoCredits?.(selectedPersona, callPayload);
            return;
        }

        setIsSubmitting(true);
        try {
            trackEvent('schedule_zvonok');
            const callData = await scheduleZvonok(callPayload);

            // Update account status (credits and history)
            onAccountStatusUpdate?.();

            // Звонок запланирован — этого достаточно. Аудио уже готово (это
            // прослушанное превью), статус виден в «Моих заказах»; онлайн
            // ждать нечего.
            onScheduled?.({
                id: callData.id,
                persona: selectedPersona,
                phone: `+7${phoneDigits}`,
                scheduledAt: new Date(scheduledMs).toISOString(),
                timezone: timeZone.id,
            });
        } catch (error) {
            if (error.data?.error === 'insufficient_credits' || error.status === 402) {
                onNoCredits?.(selectedPersona, callPayload);
                return;
            }
            if (error.status === 422) {
                showError('Проверьте номер телефона и время звонка.');
                return;
            }
            showError('Ошибка создания заказа. Попробуйте ещё раз.');
            console.error(error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const scrollPersonas = (direction) => {
        if (personasRef.current) {
            const scrollAmount = direction === 'next' ? 300 : -300;
            personasRef.current.scrollBy({left: scrollAmount, behavior: 'smooth'});
        }
    };

    return (
        <div className="max-w-[1110px] mx-auto px-4">
            <div className="bg-brand-gray rounded-[20px] md:rounded-[30px] p-0 relative">
                {step === 1 ? (
                <div className="flex flex-col lg:flex-row">
                    {/* Left side - Selectors */}
                    <div className="flex-1 min-w-0 p-4 pb-4 lg:p-[30px]">
                        {/* Persona Selector */}
                        <div className="mb-6">
                            <div className="flex items-center justify-between mb-4 pr-4 lg:pr-0">
                                <h3 className="text-xl md:text-2xl font-bold text-brand-dark leading-[1.1] tracking-[-0.24px]">
                                    Кто позвонит
                                </h3>
                                <ScrollArrows
                                    onPrev={() => scrollPersonas('prev')}
                                    onNext={() => scrollPersonas('next')}
                                />
                            </div>
                            <div className="relative overflow-hidden">
                                <div
                                    ref={personasRef}
                                    className="flex gap-[5px] overflow-x-scroll no-scrollbar py-1 pl-1"
                                    style={{scrollBehavior: 'smooth'}}
                                >
                                    {personas.map(persona => (
                                        <PersonaCard
                                            key={persona.id}
                                            persona={persona}
                                            isSelected={selectedPersona?.id === persona.id}
                                            onSelect={handleSelectPersona}
                                        />
                                    ))}
                                    <div className="flex-shrink-0 w-[100px]"/>
                                </div>
                                <div
                                    className="absolute right-0 top-0 bottom-0 w-[120px] gradient-fade-right pointer-events-none"/>
                            </div>
                        </div>

                    </div>

                    {/* Right side - text and submit */}
                    <div className="w-full lg:w-[420px] lg:flex-shrink-0">
                        <div
                            className="bg-white rounded-[20px] m-2 p-4 lg:m-[10px] lg:p-[20px] h-auto lg:h-[calc(100%-20px)] flex flex-col">

                            {/* Details input: one free-form field for the AI text */}
                            <div className="border border-dashed border-[#d3d2d5] rounded-[16px] p-3.5 mb-4">
                                <p className="text-sm font-medium text-brand-dark mb-2">Кого и с чем поздравляем</p>
                                <textarea
                                    value={aiDetails}
                                    onChange={(e) => setAiDetails(e.target.value)}
                                    placeholder="Кому звоним и по какому поводу: имя, детали..."
                                    rows={2}
                                    className="w-full px-4 py-3 rounded-[10px] border border-[#d3d2d5] text-base font-medium placeholder:text-brand-dark/50 focus:outline-none focus:border-brand-red transition bg-white resize-none"
                                />
                                <button
                                    onClick={handleGenerate}
                                    disabled={!aiDetails.trim() || isGenerating || previewLoading}
                                    className="w-full mt-2 py-3 rounded-xl bg-brand-dark text-white text-sm font-medium tracking-[-0.14px] hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                >
                                    {isGenerating ? (
                                        <>
                                            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10"
                                                        stroke="currentColor" strokeWidth="4" fill="none"/>
                                                <path className="opacity-75" fill="currentColor"
                                                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                                            </svg>
                                            Генерируем…
                                        </>
                                    ) : generatedText.trim() ? 'Составить текст заново' : 'Составить текст'}
                                </button>
                            </div>

                            {/* Текст и превью — одна карточка: сверху текст, снизу
                                приклеенная плашка озвучки. Текст меняется только через
                                Сохранить, до этого превью остаётся актуальным. */}
                            <div className="flex-1 mb-3">
                                {(generatedText.trim() || isEditingText) && (
                                    <div className="rounded-[16px] border border-[#eceaee] overflow-hidden">
                                        {isEditingText ? (
                                            <>
                                                <textarea
                                                    value={draftText}
                                                    onChange={(e) => setDraftText(e.target.value)}
                                                    placeholder="Напишите текст звонка..."
                                                    maxLength={MAX_TEXT_LENGTH}
                                                    autoFocus
                                                    aria-label="Текст звонка"
                                                    className="w-full h-[150px] px-4 pt-3 text-base font-medium text-brand-dark placeholder:text-brand-dark/50 focus:outline-none resize-none"
                                                />
                                                <div className="flex items-center gap-2 px-3 pb-3">
                                                    <button
                                                        onClick={saveEditedText}
                                                        disabled={previewLoading}
                                                        className="px-5 py-2.5 rounded-xl bg-brand-dark text-white text-sm font-medium hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        Сохранить
                                                    </button>
                                                    <button
                                                        onClick={cancelEditing}
                                                        className="px-5 py-2.5 rounded-xl border border-[#d3d2d5] bg-white text-sm font-medium text-brand-dark hover:bg-gray-50 transition"
                                                    >
                                                        Отмена
                                                    </button>
                                                    <span className="ml-auto text-sm text-brand-dark/50 tracking-[-0.14px]">
                                                        {draftText.length}/{MAX_TEXT_LENGTH}
                                                    </span>
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <p className="px-4 pt-3 pb-2 text-base font-medium text-brand-dark leading-snug whitespace-pre-wrap">
                                                    {generatedText}
                                                </p>
                                                <div className="flex items-center justify-between px-4 pb-3">
                                                    <button
                                                        onClick={startEditing}
                                                        className="flex items-center gap-1.5 text-sm font-medium text-brand-red hover:opacity-80 transition"
                                                    >
                                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                                                             stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                                                             strokeLinejoin="round">
                                                            <path d="M17 3a2.828 2.828 0 114 4L7.5 20.5 2 22l1.5-5.5L17 3z"/>
                                                        </svg>
                                                        Править текст
                                                    </button>
                                                    <span className="text-sm text-brand-dark/50 tracking-[-0.14px]">
                                                        {generatedText.length}/{MAX_TEXT_LENGTH}
                                                    </span>
                                                </div>
                                            </>
                                        )}

                                        {/* Приклеенная плашка превью */}
                                        <div className="bg-brand-red/5 px-4 py-3">
                                            {isEditingText ? (
                                                <div className="flex items-center gap-3">
                                                    <PreviewPlayCircle/>
                                                    <p className="text-sm font-medium text-brand-red/90 leading-snug">
                                                        Вы правите текст. Сохраните — и озвучим заново.
                                                    </p>
                                                </div>
                                            ) : previewLoading ? (
                                                <div className="flex items-center gap-3">
                                                    <PreviewPlayCircle loading/>
                                                    <p className="text-sm font-medium text-brand-dark/60 leading-snug">
                                                        Озвучиваем…
                                                    </p>
                                                </div>
                                            ) : previewReady ? (
                                                <button
                                                    onClick={handleTogglePlay}
                                                    className="w-full flex items-center gap-3 text-left"
                                                >
                                                    <PreviewPlayCircle active playing={previewPlaying}/>
                                                    <span className="text-sm font-medium text-brand-dark">
                                                        {previewPlaying ? 'Остановить' : 'Послушать превью'}
                                                    </span>
                                                    {preview.durationSec != null && (
                                                        <span className="ml-auto text-sm text-brand-dark/50">
                                                            {preview.durationSec} с
                                                        </span>
                                                    )}
                                                </button>
                                            ) : (
                                                <>
                                                    <div className="flex items-center gap-3 mb-3">
                                                        <PreviewPlayCircle/>
                                                        <p className="text-sm font-medium text-brand-red/90 leading-snug">
                                                            {preview ? 'Старое превью — текст или звезда изменились.' : 'Текст ещё не озвучен.'}
                                                        </p>
                                                    </div>
                                                    <button
                                                        onClick={handleRegenerateFromText}
                                                        className="w-full py-3 rounded-xl bg-gradient-to-r from-brand-red via-brand-pink to-brand-orange text-white text-sm font-medium tracking-[-0.14px] hover:opacity-95 transition flex items-center justify-center gap-2"
                                                    >
                                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                                                             stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                                                             strokeLinejoin="round">
                                                            <path d="M23 4v6h-6M1 20v-6h6"/>
                                                            <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/>
                                                        </svg>
                                                        {preview ? 'Озвучить заново' : 'Озвучить текст'}
                                                    </button>
                                                </>
                                            )}
                                            {previewError && (
                                                <p className="text-xs text-brand-red mt-2">{previewError}</p>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <button
                                onClick={() => setStep(2)}
                                disabled={!previewReady || isEditingText}
                                className="w-full py-4 rounded-2xl bg-gradient-to-r from-brand-red via-brand-pink to-brand-orange text-white text-sm font-medium tracking-[-0.14px] hover:opacity-95 transition disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Мне нравится — продолжить
                            </button>
                            {!previewReady && !isEditingText && (
                                <p className="text-xs text-brand-dark/50 mt-2 text-center">
                                    Сначала сгенерируйте превью — именно это аудио прозвучит в звонке
                                </p>
                            )}
                        </div>
                    </div>
                </div>
                ) : (
                <div className="flex flex-col lg:flex-row">
                    {/* Слева — резюме первого шага: кто звонит, что скажет,
                        превью; «Назад» сверху возвращает к тексту. */}
                    <div className="flex-1 min-w-0 p-4 pb-2 lg:p-[30px]">
                        <button
                            type="button"
                            onClick={() => setStep(1)}
                            className="flex items-center gap-1.5 text-sm text-brand-dark/60 hover:text-brand-dark transition mb-3"
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                 strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M19 12H5M12 19l-7-7 7-7"/>
                            </svg>
                            Назад
                        </button>
                        {/* На мобиле резюме прячем целиком (человек только что видел
                            текст и превью на первом шаге) — остаётся лишь «Назад». */}
                        <h3 className="hidden lg:block text-xl md:text-2xl font-bold text-brand-dark leading-[1.1] tracking-[-0.24px] mb-4">
                            Ваш звонок
                        </h3>
                        <div className="hidden lg:flex gap-4 items-start">
                            <div className="rounded-[20px] bg-white p-[10px] w-[150px] flex-shrink-0">
                                <div className="h-[190px] rounded-[10px] overflow-hidden relative">
                                    <img
                                        src={selectedPersona.image}
                                        alt={selectedPersona.name}
                                        className="w-full h-full object-cover absolute inset-0"
                                    />
                                </div>
                                <p className="pt-[10px] text-sm font-medium text-brand-dark leading-[1.3] tracking-[-0.14px]">
                                    {selectedPersona.name}
                                </p>
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm text-brand-dark/80 leading-relaxed mb-3">
                                    «{generatedText.trim()}»
                                </p>
                                <div className="flex items-center gap-2 flex-wrap">
                                    <button
                                        type="button"
                                        onClick={handleTogglePlay}
                                        className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white border-2 border-brand-red/60 text-brand-red text-sm font-medium hover:bg-brand-red/5 transition"
                                    >
                                        {previewPlaying ? (
                                            <>
                                                <svg width="12" height="12" viewBox="0 0 14 14" fill="currentColor">
                                                    <rect x="2" y="2" width="3.5" height="10" rx="1"/>
                                                    <rect x="8.5" y="2" width="3.5" height="10" rx="1"/>
                                                </svg>
                                                Остановить
                                            </>
                                        ) : (
                                            <>
                                                <svg width="12" height="12" viewBox="0 0 14 14" fill="currentColor">
                                                    <path d="M3 2l9 5-9 5V2z"/>
                                                </svg>
                                                Послушать превью
                                            </>
                                        )}
                                    </button>
                                    {preview?.durationSec != null && (
                                        <span className="text-xs text-brand-dark/50">
                                            {preview.durationSec} с
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Справа — куда и когда позвонить. */}
                    <div className="w-full lg:w-[420px] lg:flex-shrink-0">
                        <div className="bg-white rounded-[20px] m-2 p-4 lg:m-[10px] lg:p-[20px] h-auto lg:h-[calc(100%-20px)] flex flex-col">
                        <h3 className="text-xl md:text-2xl font-bold text-brand-dark leading-[1.1] tracking-[-0.24px] mb-4">
                            Куда и когда позвонить
                        </h3>

                        <div className="flex flex-col gap-2">
                            <div>
                                <input
                                    ref={phoneInputRef}
                                    type="tel"
                                    inputMode="tel"
                                    value={formatPhoneDisplay(phoneDigits)}
                                    onChange={(e) => {
                                        setPhoneDigits(extractPhoneDigits(e.target.value));
                                        if (phoneError) setPhoneError(null);
                                    }}
                                    placeholder="+7 (999) 123-45-67"
                                    aria-invalid={!!phoneError}
                                    className={`w-full px-4 py-3 rounded-[10px] border text-base font-medium placeholder:text-brand-dark/50 focus:outline-none transition bg-white ${
                                        phoneError
                                            ? 'border-brand-red focus:border-brand-red'
                                            : 'border-[#d3d2d5] focus:border-brand-red'
                                    }`}
                                />
                                {phoneError && (
                                    <p className="text-xs text-brand-red mt-1.5">{phoneError}</p>
                                )}
                            </div>
                            <div>
                                <input
                                    ref={dateInputRef}
                                    type="datetime-local"
                                    value={scheduledAtLocal}
                                    min={minDateTime}
                                    onChange={(e) => {
                                        setScheduledAtLocal(e.target.value);
                                        if (dateError) setDateError(null);
                                    }}
                                    aria-label="Дата и время звонка"
                                    aria-invalid={!!dateError}
                                    className={`w-full px-4 py-3 rounded-[10px] border text-base font-medium text-brand-dark focus:outline-none transition bg-white ${
                                        dateError
                                            ? 'border-brand-red focus:border-brand-red'
                                            : 'border-[#d3d2d5] focus:border-brand-red'
                                    }`}
                                />
                                {dateError && (
                                    <p className="text-xs text-brand-red mt-1.5">{dateError}</p>
                                )}
                            </div>
                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    onClick={() => setQuickDate(0)}
                                    className={`px-4 py-1.5 rounded-lg text-sm font-medium transition ${
                                        isQuickDateActive(0)
                                            ? 'bg-brand-red/10 text-brand-red ring-1 ring-brand-red/40'
                                            : 'bg-white text-brand-dark hover:bg-gray-50 border border-[#d3d2d5]'
                                    }`}
                                >
                                    Сегодня
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setQuickDate(1)}
                                    className={`px-4 py-1.5 rounded-lg text-sm font-medium transition ${
                                        isQuickDateActive(1)
                                            ? 'bg-brand-red/10 text-brand-red ring-1 ring-brand-red/40'
                                            : 'bg-white text-brand-dark hover:bg-gray-50 border border-[#d3d2d5]'
                                    }`}
                                >
                                    Завтра
                                </button>
                                <select
                                    value={timeZone.id}
                                    onChange={(e) => {
                                        setTimeZone(findRuTimezone(e.target.value) || timeZone);
                                        setDateError(null);
                                    }}
                                    aria-label="Часовой пояс"
                                    className="flex-1 min-w-0 px-2 py-1.5 rounded-lg border border-[#d3d2d5] bg-white text-sm font-medium text-brand-dark focus:outline-none focus:border-brand-red transition"
                                >
                                    {RU_TIMEZONES.map(zone => (
                                        <option key={zone.id} value={zone.id}>{zone.label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <p className="text-xs text-brand-dark/50 mt-2 leading-snug">
                            Позвоним точно в выбранное время. Если абонент не возьмёт трубку,
                            перезвоним ещё два раза — иначе вернём кредит.
                        </p>

                        <button
                            onClick={handleSubmit}
                            disabled={!previewReady || isSubmitting}
                            className="w-full mt-4 py-4 rounded-2xl bg-gradient-to-r from-brand-red via-brand-pink to-brand-orange text-white text-sm font-medium tracking-[-0.14px] hover:opacity-95 transition disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isSubmitting
                                ? 'Планируем звонок…'
                                : credits > 0
                                    ? `Запланировать звонок (${credits} осталось)`
                                    : 'Запланировать звонок'
                            }
                        </button>
                        </div>
                    </div>
                </div>
                )}
            </div>
        </div>
    );
}
